/**
 * Der Offline-Nachweis (PV-B30).
 *
 * Die Anforderung steht so in der Spec: Die Offline-Faehigkeit wird auf dem
 * tatsaechlichen Zielgeraet nachgewiesen, bevor Stufe 1 als fertig gilt.
 * Offline-Paket laden, Flugmodus, einen Tag ruhen lassen, oeffnen, vollstaendige
 * Aufnahme durchspielen, wieder verbinden, Abgleich pruefen.
 *
 * Warum das ein Punkt im Programm ist und nicht ein Zettel: Der Kern der Pruefung
 * ist die Frage, ob das Betriebssystem den lokalen Bestand ueber Nacht behalten
 * hat. Das kann niemand von aussen sehen — das Geraet selbst muss vorher
 * festhalten, was da war, und hinterher nachzaehlen. Apple ist hier strenger als
 * Android und Windows, und genau daran kann der ganze Ansatz kippen.
 *
 * Der Nachweis bleibt auf dem Geraet. Er wird nicht abgeglichen, weil er eine
 * Aussage ueber dieses eine Geraet ist und ueber kein anderes.
 */

import { db, leseZustand, schreibeZustand } from './db.ts';
import { speicherstand } from './speicher.ts';

export const NACHWEIS = 'offline.nachweis';

export type Schrittstand = 'offen' | 'erledigt' | 'fehlgeschlagen';

export interface Bestandsaufnahme {
  projekte: number;
  flaechen: number;
  belegungen: number;
  module: number;
  gebaeude: number;
  fotos: number;
  kachelprojekte: number;
  belegtBytes?: number;
}

export interface Nachweisschritt {
  schluessel: string;
  text: string;
  hinweis?: string;
  stand: Schrittstand;
  am?: string;
  bemerkung?: string;
}

export interface Offlinenachweis {
  geraet: string;
  begonnenAm?: string;
  /** Was beim Vorbereiten da war — die Messlatte fuer den Tag danach. */
  bestandVorher?: Bestandsaufnahme;
  bestandNachher?: Bestandsaufnahme;
  dauerhafterSpeicher?: boolean;
  /** Wurde die Anwendung tatsaechlich einmal ohne Verbindung gestartet? */
  startOhneVerbindungAm?: string;
  schritte: Nachweisschritt[];
  abgeschlossenAm?: string;
  ergebnis?: 'bestanden' | 'nicht_bestanden';
}

/** Die Schritte, genau in der Reihenfolge, die die Spec vorgibt. */
const SCHRITTE: Array<Omit<Nachweisschritt, 'stand'>> = [
  {
    schluessel: 'paket',
    text: 'Offline-Paket geladen',
    hinweis: 'In der Projektliste "Vorbereiten" — Kacheln, Gebäudeumrisse, Stammdaten.',
  },
  {
    schluessel: 'speicher',
    text: 'Dauerhafter Speicher gewährt',
    hinweis: 'Wird beim Start angefordert. Lehnt das Gerät ab, ist es nicht verlässlich nutzbar.',
  },
  {
    schluessel: 'flugmodus',
    text: 'Flugmodus eingeschaltet',
    hinweis: 'Wirklich das Gerät, nicht nur den Browser.',
  },
  {
    schluessel: 'ruhen',
    text: 'Einen Tag ruhen lassen',
    hinweis: 'Der eigentliche Punkt: Verwirft das Betriebssystem den Bestand über Nacht?',
  },
  {
    schluessel: 'oeffnen',
    text: 'Ohne Verbindung geöffnet',
    hinweis: 'Das Programm muss anlaufen, ohne den Server zu fragen.',
  },
  {
    schluessel: 'aufnahme',
    text: 'Vollständige Aufnahme durchgespielt',
    hinweis: 'Fläche zeichnen, Traufkante, Neigung, Modul belegen, Foto machen.',
  },
  {
    schluessel: 'verbinden',
    text: 'Wieder verbunden',
  },
  {
    schluessel: 'abgleich',
    text: 'Abgleich geprüft',
    hinweis: 'Alles, was offline entstanden ist, muss angekommen sein.',
  },
];

export async function bestandAufnehmen(): Promise<Bestandsaufnahme> {
  const [projekte, flaechen, belegungen, module, gebaeude, fotos, kachelprojekte] =
    await Promise.all([
      db.projekte.count(),
      db.flaechen.count(),
      db.belegungen.count(),
      db.module.count(),
      db.gebaeudeumrisse.count(),
      db.fotos.count(),
      db.kachelstand.count(),
    ]);
  const { belegtBytes } = await speicherstand();
  return {
    projekte,
    flaechen,
    belegungen,
    module,
    gebaeude,
    fotos,
    kachelprojekte,
    ...(belegtBytes !== undefined ? { belegtBytes } : {}),
  };
}

export async function leseNachweis(geraet: string): Promise<Offlinenachweis> {
  const gespeichert = await leseZustand<Offlinenachweis | null>(NACHWEIS, null);
  if (gespeichert) return gespeichert;
  return { geraet, schritte: SCHRITTE.map((s) => ({ ...s, stand: 'offen' })) };
}

export async function schreibeNachweis(nachweis: Offlinenachweis): Promise<void> {
  await schreibeZustand(NACHWEIS, nachweis);
}

/** Prueflauf beginnen: Bestand festhalten, an dem spaeter gemessen wird. */
export async function beginne(geraet: string): Promise<Offlinenachweis> {
  const nachweis: Offlinenachweis = {
    geraet,
    begonnenAm: new Date().toISOString(),
    bestandVorher: await bestandAufnehmen(),
    schritte: SCHRITTE.map((s) => ({ ...s, stand: 'offen' })),
  };
  await schreibeNachweis(nachweis);
  return nachweis;
}

export async function setzeSchritt(
  nachweis: Offlinenachweis,
  schluessel: string,
  stand: Schrittstand,
  bemerkung?: string,
): Promise<Offlinenachweis> {
  const neu: Offlinenachweis = {
    ...nachweis,
    schritte: nachweis.schritte.map((schritt) =>
      schritt.schluessel === schluessel
        ? {
            ...schritt,
            stand,
            am: new Date().toISOString(),
            ...(bemerkung !== undefined ? { bemerkung } : {}),
          }
        : schritt,
    ),
  };
  await schreibeNachweis(neu);
  return neu;
}

/**
 * Beim Start festhalten, ob ohne Verbindung gestartet wurde.
 *
 * Das ist die einzige Angabe, die sich nicht nachtraeglich behaupten laesst —
 * sie muss in dem Moment entstehen, in dem es passiert.
 */
export async function vermerkeStart(online: boolean): Promise<void> {
  if (online) return;
  const nachweis = await leseZustand<Offlinenachweis | null>(NACHWEIS, null);
  if (!nachweis || nachweis.abgeschlossenAm) return;
  await schreibeNachweis({
    ...nachweis,
    startOhneVerbindungAm: new Date().toISOString(),
    schritte: nachweis.schritte.map((schritt) =>
      schritt.schluessel === 'oeffnen' && schritt.stand === 'offen'
        ? {
            ...schritt,
            stand: 'erledigt',
            am: new Date().toISOString(),
            bemerkung: 'vom Programm festgestellt',
          }
        : schritt,
    ),
  });
}

export interface Bestandsvergleich {
  feld: keyof Bestandsaufnahme;
  vorher: number;
  nachher: number;
  verloren: boolean;
}

/**
 * Hat das Geraet den Bestand behalten?
 *
 * Weniger als vorher heisst: Das Betriebssystem hat verworfen. Genau dafuer ist
 * die ganze Pruefung da — alles andere kann man auch am Entwicklungsrechner
 * sehen.
 */
export function vergleiche(
  vorher: Bestandsaufnahme | undefined,
  nachher: Bestandsaufnahme | undefined,
): Bestandsvergleich[] {
  if (!vorher || !nachher) return [];
  const felder: Array<keyof Bestandsaufnahme> = [
    'projekte',
    'flaechen',
    'belegungen',
    'module',
    'gebaeude',
    'fotos',
    'kachelprojekte',
  ];
  return felder.map((feld) => ({
    feld,
    vorher: vorher[feld] ?? 0,
    nachher: nachher[feld] ?? 0,
    verloren: (nachher[feld] ?? 0) < (vorher[feld] ?? 0),
  }));
}

/** Abschliessen und werten. */
export async function schliesseAb(nachweis: Offlinenachweis): Promise<Offlinenachweis> {
  const bestandNachher = await bestandAufnehmen();
  const verlust = vergleiche(nachweis.bestandVorher, bestandNachher).some((v) => v.verloren);
  const alleErledigt = nachweis.schritte.every((s) => s.stand === 'erledigt');
  const neu: Offlinenachweis = {
    ...nachweis,
    bestandNachher,
    abgeschlossenAm: new Date().toISOString(),
    ergebnis: !verlust && alleErledigt ? 'bestanden' : 'nicht_bestanden',
  };
  await schreibeNachweis(neu);
  return neu;
}
